import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Message } from '@qtek/shared/models';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { ItemActions } from './item.actions';
import { ItemService } from './item.service';

@Injectable()
export class ItemEffects {
  /** When backend doesn't echo mysid in get response, correlate by request id so we store under correct mysid. */
  private pendingGetMysidById = new Map<string, string>();

  constructor(
    private actions$: Actions,
    private itemService: ItemService
  ) {}

  connect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.connectItem, ItemActions.subscribeItem),
      mergeMap(action => {
        const payload =
          action.type === ItemActions.connectItem.type
            ? action.payload
            : {
                q: action.q,
                view: action.view,
                mysid: action.mysid,
                ...(action.prms !== undefined ? { prms: action.prms } : {}),
                ...(action.op ? { op: action.op } : {}),
              };

        // For RELATED_DEAL_LOOKUP, also accept 'get' responses (backend may not echo mysid)
        const messageFilter =
          payload.mysid === 'task_form_related_deal_lookup'
            ? (message: any) =>
                message.ent === this.itemService.entityName &&
                Boolean(message.op) &&
                (message.mysid === payload.mysid || message.op === 'get')
            : undefined;
        return this.itemService.subscribe(payload, messageFilter).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(ItemActions.disconnectItem, ItemActions.unsubscribeItem),
              filter(disconnectAction => {
                if ('payload' in disconnectAction) {
                  return disconnectAction.payload.mysid === payload.mysid;
                }

                return disconnectAction.mysid === payload.mysid;
              })
            )
          )
        );
      }),
      filter((data: any) => data.op),
      map((data: any) => {
        const { res, op, sts, mysid } = data;
        switch (op) {
          case 'query':
          case 'fast_query': {
            // RELATED_DEAL_LOOKUP is only for getItem results; never overwrite with query/fast_query (backend may send delayed query and wipe the deal name)
            if (mysid === 'task_form_related_deal_lookup') {
              return { type: 'NOOP' };
            }
            return res
              ? ItemActions.getItemsSuccess({
                  res: res || [],
                  meta: sts?.meta,
                  mysid,
                })
              : ItemActions.getItemsFailure({ payload: sts });
          }
          case 'get': {
            // Backend may return res as single item or as [item]; reducer expects single Item
            const item = Array.isArray(res) ? res[0] : res;
            // If backend doesn't echo mysid, use pending map (filled when getItem was dispatched)
            const responseId = data.id ?? item?._id;
            const mysidRes =
              mysid ?? (responseId && this.pendingGetMysidById.get(responseId));
            if (responseId) {
              this.pendingGetMysidById.delete(responseId);
            }
            const mysidFinal = mysidRes ?? mysid;
            if (item && mysidFinal) {
              return ItemActions.getItemSuccess({
                res: item,
                mysid: mysidFinal,
              });
            }
            if (item) {
              return { type: 'NOOP' }; // get response for another consumer, ignore
            }
            return ItemActions.getItemFailure({ payload: sts });
          }
          case 'ins':
            return res
              ? ItemActions.createItemSuccess({
                  res: res || [],
                  mysid,
                  message: new Message('GNR_ITM_CREATED'),
                })
              : ItemActions.createItemFailure({ payload: sts });
          case 'upd':
            return res
              ? ItemActions.updateItemSuccess({
                  res: res || [],
                  mysid,
                  message: new Message('GNR_ITM_UPDATED'),
                })
              : ItemActions.updateItemFailure({ payload: sts });
          case 'del':
            return res
              ? ItemActions.deleteItemSuccess({
                  res: res || [],
                  mysid,
                  message: new Message('GNR_ITM_DELETED'),
                })
              : ItemActions.deleteItemFailure({ payload: sts });
          default:
            return { type: 'NOT_EXIST' };
        }
      })
    )
  );

  items$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.getItems),
        tap(({ payload }) => this.itemService.getItems(payload))
      ),
    { dispatch: false }
  );

  getItemById$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.getItem),
        tap(({ id, view, mysid }) => {
          if (id && mysid) {
            this.pendingGetMysidById.set(id, mysid);
          }
          this.itemService.getItemById({ id, view, mysid });
        })
      ),
    { dispatch: false }
  );

  createItem$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.createItem),
        tap(({ payload }) => this.itemService.createItem({ res: payload }))
      ),
    { dispatch: false }
  );

  updateItem$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.updateItem),
        tap(({ payload, id, mysid }) =>
          this.itemService.updateItem({
            res: payload,
            id: id,
            mysid: mysid,
          })
        )
      ),
    { dispatch: false }
  );

  deleteItem$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.deleteItem),
        tap(({ payload }) => this.itemService.deleteItem({ id: payload }))
      ),
    { dispatch: false }
  );

  mintItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.mintItem),
      switchMap(({ payload }) =>
        this.itemService.mintItem(payload).pipe(
          map(({ res }) => ItemActions.mintItemSuccess({ payload: res })),
          catchError(({ sts }) =>
            of(ItemActions.mintItemFailure({ payload: sts }))
          )
        )
      )
    )
  );

  sendNdaEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.sendNdaEvent),
      switchMap(({ parId, userApproval }) =>
        this.itemService.sendNdaEvent(parId, userApproval).pipe(
          map(({ res }) => ItemActions.sendNdaEventSuccess({ payload: res })),
          catchError(({ sts }) =>
            of(ItemActions.sendNdaEventFailure({ payload: sts }))
          )
        )
      )
    )
  );
}
